import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import useMemberDisplayNames from '../member-display/useMemberDisplayNames';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { journeyApi } from '../../api/journeyApi';
import { journeyPlanningApi } from '../../api/journeyPlanningApi';
import { journeyAccommodationApi } from '../../api/journeyAccommodationApi';
import { journeyTransportApi } from '../../api/journeyTransportApi';
import { travelMediaApi } from '../../api/travelMediaApi';
import { useAuth } from '../../auth/AuthContext';
import { ErrorState, LoadingState } from '../../components/ui';
import { encodeDestinationSummary } from '../my-journeys/myJourneysModel';
import {
  classifyJourneyAccessError,
  destinationTextFromSummary,
  normalizeJourneyWorkspace,
} from './journeyWorkspaceModel';
import {
  buildLifecycleMutationBody,
  classifyLifecycleMutationError,
  lifecycleActionKeys,
  isKnownLifecycleStatus,
  lifecycleHintKey,
  lifecycleMutationErrorKey,
  lifecycleStatusKey,
  primaryLifecycleAction,
  secondaryLifecycleActions,
} from './journeyLifecycleModel';
import JourneyWorkspaceNav from './JourneyWorkspaceNav';
import { useTravelText } from '../../app/TravelLocaleContext';
import JourneyContinuationSourceNote from './JourneyContinuationSourceNote';
import JourneyGuidedTasks from './JourneyGuidedTasks';
import { WORKSPACE_PRODUCT_CHROME } from './journeyWorkspacePresentation';
import { formatPlainDate, formatPlainDateRange } from '../../i18n/travelFormat';
import { useJourneyNoticeUnreadCount } from '../journey-notice/useJourneyNoticeUnreadCount';
import JourneyPublicationSection from '../journey-publication/JourneyPublicationSection';
import JourneyPeopleInviteSection from '../journey-invitation/JourneyPeopleInviteSection';
import JourneyPeopleMembershipPanel from './JourneyPeopleMembershipPanel';
import JourneySpatialOverviewSection from '../journey-spatial/JourneySpatialOverviewSection';
import {
  SPATIAL_PHASE,
} from '../journey-spatial/journeySpatialOverviewModel';
import { normalizePlanningSchedule } from '../journey-planning/planningModel';
import { normalizeAccommodationSummary } from '../journey-accommodation/accommodationModel';
import { normalizeTransportSummary } from '../journey-transport/transportModel';
import OfflineReadBanner from '../offline/OfflineReadBanner';
import {
  OFFLINE_FRESHNESS,
  OFFLINE_READ_COPY,
} from '../offline/offlineFreshness';
import {
  mayUseJourneyOfflineFallback,
  readOverviewOfflineSnapshot,
  writeOverviewOfflineSnapshot,
  hasUsableTodayOfflineSnapshot,
  clearJourneyOfflineSnapshotsAfterAuthoritativeDenial,
} from '../offline/offlineJourneyRead';
import './journey-workspace.css';
import '../journey-invitation/journey-invitation.css';
import '../journey-decision/journey-decision.css';
import '../journey-journal/journey-journal.css';

function sectionFromPath(pathname) {
  if (pathname.endsWith('/people')) return 'people';
  return 'overview';
}

function runLifecycleApi(action, journeyId, body) {
  if (action === 'READY') return journeyApi.markReady(journeyId, body);
  if (action === 'START') return journeyApi.start(journeyId, body);
  if (action === 'COMPLETE') return journeyApi.complete(journeyId, body);
  if (action === 'CANCEL') return journeyApi.cancel(journeyId, body);
  if (action === 'ARCHIVE') return journeyApi.archive(journeyId, body);
  throw new Error(`Unsupported lifecycle action: ${action}`);
}

export default function JourneyWorkspacePage() {
  const { journeyId } = useParams();
  const location = useLocation();
  const { t, intlLocale } = useTravelText();
  // 工作區頁首／基本資料／封面文案：有 key 用翻譯，缺 key 回退既有繁中 chrome（其他工作區頁仍直接用 chrome）
  const chrome = useMemo(() => Object.fromEntries(Object.keys(WORKSPACE_PRODUCT_CHROME).map((name) => {
    const key = `travel.journeyWorkspace.chrome.${name}`;
    const value = t(key);
    return [name, value === key ? WORKSPACE_PRODUCT_CHROME[name] : value];
  })), [t]);
  // load 以 ref 取最新翻譯，切換語系不重新載入資料
  const tRef = useRef(t);
  tRef.current = t;
  const section = sectionFromPath(location.pathname);
  const { user } = useAuth();
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const saving = useRef(false);
  const lifecycleBusy = useRef(false);
  const disposed = useRef(false);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [errorModel, setErrorModel] = useState(null);
  const [journey, setJourney] = useState(null);
  const [editing, setEditing] = useState(false);
  // 本地錯誤存 key 或已遮蔽的原因，呈現時翻譯，切換語系即更新
  const [saveError, setSaveError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lifecyclePending, setLifecyclePending] = useState(false);
  const [lifecycleError, setLifecycleError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [readSource, setReadSource] = useState('live');
  const [offlineFreshness, setOfflineFreshness] = useState(null);
  const [offlineFetchedAt, setOfflineFetchedAt] = useState('');
  const [todayOfflineAvailable, setTodayOfflineAvailable] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [accommodations, setAccommodations] = useState([]);
  const [transports, setTransports] = useState([]);
  const [spatialPhase, setSpatialPhase] = useState(SPATIAL_PHASE.IDLE);
  const { unreadCount } = useJourneyNoticeUnreadCount(journeyId);
  const memberSummaries = useMemberDisplayNames(
    (journey?.members || []).map((member) => member.memberId),
  );
  const [draft, setDraft] = useState({
    title: '', description: '', destination: '', startDate: '', endDate: '',
  });
  // PD-COV-01: journey cover upload state (PATCH coverFileReference — existing API)
  const [coverPhase, setCoverPhase] = useState('idle'); // 'idle' | 'uploading' | 'error'
  const [coverError, setCoverError] = useState(null);
  const coverInputRef = useRef(null);
  // Dedicated token so cover upload is not cancelled by soft load / online refresh generation bumps.
  const coverOpGeneration = useRef(0);

  const offlineCached = readSource === 'cached';

  const titleId = useId();
  const descriptionId = useId();
  const destinationId = useId();
  const startId = useId();
  const endId = useId();
  const lifecycleTitleId = useId();
  const confirmTitleId = useId();
  const cancelReasonId = useId();

  const applyLiveOverview = useCallback((payload) => {
    const normalized = normalizeJourneyWorkspace(payload, viewerMemberId);
    setJourney(normalized);
    setDraft({
      title: normalized.title,
      description: normalized.description,
      destination: destinationTextFromSummary(normalized.destinationSummaryJson),
      startDate: normalized.startDate || '',
      endDate: normalized.endDate || '',
    });
    setReadSource('live');
    setOfflineFreshness(null);
    setOfflineFetchedAt('');
    setPhase('ready');
  }, [viewerMemberId]);

  /** Stay Pins：accommodations 為 Overview 增強；失敗不拖垮總覽／Activity map。 */
  const loadAccommodations = useCallback(async (requestId) => {
    try {
      const raw = await journeyAccommodationApi.list(journeyId);
      if (requestId !== generation.current) return;
      const rows = Array.isArray(raw) ? raw : [];
      setAccommodations(rows.map(normalizeAccommodationSummary));
    } catch {
      if (requestId !== generation.current) return;
      setAccommodations([]);
    }
  }, [journeyId]);

  /** Transport：僅文字 logistics；失敗不拖垮總覽／地圖。 */
  const loadTransports = useCallback(async (requestId) => {
    try {
      const raw = await journeyTransportApi.list(journeyId);
      if (requestId !== generation.current) return;
      const rows = Array.isArray(raw) ? raw : [];
      setTransports(rows.map(normalizeTransportSummary));
    } catch {
      if (requestId !== generation.current) return;
      setTransports([]);
    }
  }, [journeyId]);

  /** AUTH59：schedule 為 Overview 地理增強；失敗不拖垮總覽核心。 */
  const loadSpatialSchedule = useCallback(async (requestId, journeyRaw) => {
    setSpatialPhase(SPATIAL_PHASE.LOADING);
    try {
      const raw = await journeyPlanningApi.getSchedule(journeyId);
      if (requestId !== generation.current) return;
      setSchedule(normalizePlanningSchedule(raw));
      setSpatialPhase(SPATIAL_PHASE.READY);
      // additive warm：同一 authoritative journey + 成功 schedule；不另打 detail
      writeOverviewOfflineSnapshot({
        memberId: viewerMemberId,
        journeyId,
        journeyRaw,
        scheduleRaw: raw,
      }).catch(() => {
        /* cache write must not break live Overview */
      });
    } catch {
      if (requestId !== generation.current) return;
      setSchedule(null);
      // 失敗不寫入／不覆寫 last-good schedule；僅清 live spatial UI
      setSpatialPhase(SPATIAL_PHASE.UNAVAILABLE);
    }
  }, [journeyId, viewerMemberId]);

  const load = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    if (!soft) {
      setPhase('loading');
      setEditing(false);
      setSaveError(null);
      setSchedule(null);
      setAccommodations([]);
      setTransports([]);
      setSpatialPhase(SPATIAL_PHASE.IDLE);
    }
    setError('');
    setErrorModel(null);
    try {
      const payload = await journeyApi.getDetail(journeyId);
      if (requestId !== generation.current) return;
      applyLiveOverview(payload);
      // progressive：先暖 journey；若先前已有 schedule 則 compose 保留 last-good
      writeOverviewOfflineSnapshot({
        memberId: viewerMemberId,
        journeyId,
        journeyRaw: payload,
      }).catch(() => {
        /* cache write must not break live Overview */
      });
      hasUsableTodayOfflineSnapshot({ memberId: viewerMemberId, journeyId })
        .then((ok) => {
          if (requestId === generation.current) setTodayOfflineAvailable(ok);
        })
        .catch(() => {
          if (requestId === generation.current) setTodayOfflineAvailable(false);
        });
      // progressive：detail 成功後再拉 schedule／accommodations／transports；失敗各自隔離
      loadSpatialSchedule(requestId, payload);
      loadAccommodations(requestId);
      loadTransports(requestId);
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyJourneyAccessError(cause);
      if (kind === 'not-found' || kind === 'forbidden') {
        setReadSource('live');
        setSchedule(null);
        setAccommodations([]);
        setTransports([]);
        setSpatialPhase(SPATIAL_PHASE.IDLE);
        if (viewerMemberId) {
          await clearJourneyOfflineSnapshotsAfterAuthoritativeDenial({
            memberId: viewerMemberId,
            journeyId,
          });
        }
        if (requestId !== generation.current) return;
        setPhase(kind === 'not-found' ? 'not-found' : 'forbidden');
        return;
      }
      if (mayUseJourneyOfflineFallback(cause) && viewerMemberId) {
        try {
          const cached = await readOverviewOfflineSnapshot({
            memberId: viewerMemberId,
            journeyId,
          });
          if (requestId !== generation.current) return;
          if (cached?.record?.payload?.journey) {
            const normalized = normalizeJourneyWorkspace(
              cached.record.payload.journey,
              viewerMemberId,
            );
            setJourney(normalized);
            setDraft({
              title: normalized.title,
              description: normalized.description,
              destination: destinationTextFromSummary(normalized.destinationSummaryJson),
              startDate: normalized.startDate || '',
              endDate: normalized.endDate || '',
            });
            setReadSource('cached');
            setOfflineFreshness(cached.freshness);
            setOfflineFetchedAt(cached.record.fetchedAt || '');
            setEditing(false);
            const cachedSchedule = cached.record.payload.schedule;
            if (cachedSchedule != null) {
              setSchedule(normalizePlanningSchedule(cachedSchedule));
            } else {
              setSchedule(null);
            }
            setSpatialPhase(SPATIAL_PHASE.OFFLINE);
            const todayOk = await hasUsableTodayOfflineSnapshot({
              memberId: viewerMemberId,
              journeyId,
            });
            if (requestId !== generation.current) return;
            setTodayOfflineAvailable(todayOk);
            setPhase('ready');
            return;
          }
          if (cached?.freshness === OFFLINE_FRESHNESS.EXPIRED) {
            setError(OFFLINE_READ_COPY.expiredBody);
            setErrorModel(null);
            setPhase('error');
            setReadSource('live');
            return;
          }
        } catch {
          /* fall through to network error UX */
        }
      }
      setReadSource('live');
      setSchedule(null);
      setSpatialPhase(SPATIAL_PHASE.IDLE);
      setError('');
      setErrorModel(mapApiErrorToUiModel(cause, { t: tRef.current }));
      setPhase('error');
    }
  }, [applyLiveOverview, journeyId, loadAccommodations, loadSpatialSchedule, loadTransports, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    disposed.current = false;
    load();
    const onOnline = () => {
      load({ soft: true });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline);
    }
    return () => {
      disposed.current = true;
      generation.current += 1;
      coverOpGeneration.current += 1;
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
      }
    };
  }, [journeyId, load]);

  const searchParams = new URLSearchParams(location.search);
  if (searchParams.get('section') === 'planning') {
    return <Navigate to={`/journeys/${journeyId}/planning`} replace />;
  }
  if (searchParams.get('section') === 'people') {
    return <Navigate to={`/journeys/${journeyId}/people`} replace />;
  }

  async function saveBasic(event) {
    event.preventDefault();
    if (!journey || saving.current || offlineCached) return;
    const title = draft.title.trim();
    if (!title) {
      setSaveError({ key: 'travel.journeyWorkspace.chrome.save.titleRequired' });
      return;
    }
    if (draft.startDate && draft.endDate && draft.endDate < draft.startDate) {
      setSaveError({ key: 'travel.journeyWorkspace.chrome.save.endBeforeStart' });
      return;
    }

    saving.current = true;
    setBusy(true);
    setSaveError(null);
    try {
      await journeyApi.updateBasicInformation(journey.journeyId, {
        expectedRowVersion: journey.rowVersion,
        title,
        description: draft.description.trim() || null,
        destinationSummaryJson: encodeDestinationSummary(draft.destination),
        startDate: draft.startDate || null,
        endDate: draft.endDate || null,
      });
      setEditing(false);
      await load({ soft: true });
      return;
    } catch (cause) {
      const kind = classifyJourneyAccessError(cause);
      if (kind === 'conflict') {
        setSaveError({ key: 'travel.journeyWorkspace.chrome.save.conflict' });
      } else if (kind === 'validation') {
        setSaveError({ key: 'travel.journeyWorkspace.chrome.save.validation' });
      } else if (kind === 'forbidden') {
        setSaveError({ key: 'travel.journeyWorkspace.chrome.save.forbidden' });
      } else {
        setSaveError({ cause });
      }
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }

  /**
   * PD-COV-01: Upload a cover image and PATCH coverFileReference.
   * Uses existing PATCH /journeys/{id}/basic-information (coverFileReference JsonPatchField).
   * Cover is NOT linked to SocialPost media; displayOrder=0 rule does NOT apply here.
   */
  async function handleCoverFileChange(event) {
    const file = event.target.files?.[0];
    if (!file || !journey || offlineCached) return;
    // Reset input so the same file can be re-selected after error
    event.target.value = '';
    // Dedicated cover token: invalidates prior cover ops only (not soft load / online refresh).
    const requestId = ++coverOpGeneration.current;
    setCoverPhase('uploading');
    setCoverError(null);
    try {
      const objectKey = await travelMediaApi.uploadImage(file);
      if (requestId !== coverOpGeneration.current || disposed.current) return;
      await journeyApi.updateBasicInformation(journey.journeyId, {
        expectedRowVersion: journey.rowVersion,
        coverFileReference: objectKey,
      });
      if (requestId !== coverOpGeneration.current || disposed.current) return;
      setCoverPhase('idle');
      await load({ soft: true });
    } catch {
      if (requestId !== coverOpGeneration.current || disposed.current) return;
      setCoverPhase('error');
      setCoverError('travel.journeyWorkspace.chrome.coverFailed');
    }
  }

  function retryCoverUpload() {
    setCoverPhase('idle');
    setCoverError(null);
    coverInputRef.current?.click();
  }

  /** 任務卡「基本資料」沿用既有編輯表單；有指定缺漏欄位時聚焦該欄，否則聚焦標題欄位。 */
  function openBasicEditor(focusField) {
    const fieldIds = { destination: destinationId, startDate: startId, endDate: endId };
    const targetId = fieldIds[focusField] || titleId;
    setEditing(true);
    setSaveError(null);
    setTimeout(() => document.getElementById(targetId)?.focus(), 0);
  }

  function openLifecycleConfirm(action) {
    if (!action || lifecycleBusy.current || busy || offlineCached) return;
    setLifecycleError('');
    setCancelReason('');
    setConfirmAction(action);
  }

  function closeLifecycleConfirm() {
    if (lifecycleBusy.current) return;
    setConfirmAction(null);
    setCancelReason('');
  }

  async function submitLifecycle() {
    if (!journey || !confirmAction || lifecycleBusy.current || offlineCached) return;
    const action = confirmAction;
    const requestId = generation.current;
    const body = buildLifecycleMutationBody(journey.rowVersion, action, {
      reason: action === 'CANCEL' ? cancelReason : undefined,
    });
    lifecycleBusy.current = true;
    setLifecyclePending(true);
    setLifecycleError('');
    try {
      await runLifecycleApi(action, journey.journeyId, body);
      if (requestId !== generation.current) return;
      setConfirmAction(null);
      setCancelReason('');
      await load({ soft: true });
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyLifecycleMutationError(cause);
      const message = lifecycleMutationErrorKey(kind);
      if (kind === 'conflict' || kind === 'invalid-lifecycle') {
        setConfirmAction(null);
        setCancelReason('');
        setLifecycleError(message);
        await load({ soft: true });
      } else if (kind === 'not-found') {
        setConfirmAction(null);
        setCancelReason('');
        setPhase('not-found');
      } else if (kind === 'forbidden') {
        setConfirmAction(null);
        setCancelReason('');
        setLifecycleError(message);
      } else {
        setLifecycleError(message);
      }
    } finally {
      lifecycleBusy.current = false;
      if (!disposed.current) setLifecyclePending(false);
    }
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state">
        <LoadingState label={t('travel.journeyWorkspace.loading')} />
      </main>
    );
  }

  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>{chrome.notFoundTitle}</h1>
        <p>{chrome.notFoundBody}</p>
        <Link to="/journeys">{chrome.backToMyJourneys}</Link>
      </main>
    );
  }

  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <h1>{chrome.forbiddenTitle}</h1>
        <p>{chrome.forbiddenBody}</p>
        <Link to="/journeys">{chrome.backToMyJourneys}</Link>
      </main>
    );
  }

  if (phase === 'error') {
    if (errorModel) {
      const errorCopy = resolveUiErrorCopy(errorModel, t);
      return (
        <main className="journey-workspace-state" data-testid="workspace-error">
          <ErrorState
            title={chrome.openFailedTitle}
            description={errorCopy.description}
            retryable
            retryLabel={chrome.tryAgain}
            onRetry={load}
          />
        </main>
      );
    }
    return (
      <main className="journey-workspace-state" role="alert" data-testid="workspace-error">
        <h1>{chrome.openFailedTitle}</h1>
        <p>{error}</p>
        <button type="button" onClick={load}>{chrome.tryAgain}</button>
      </main>
    );
  }

  const primaryAction = primaryLifecycleAction(journey);
  const secondaryActions = secondaryLifecycleActions(journey);
  const confirmCopy = confirmAction ? lifecycleActionKeys(confirmAction) : null;
  // 未知狀態沿用既有行為：顯示原始狀態值；空值才顯示「未知狀態」
  const statusLabel = isKnownLifecycleStatus(journey.lifecycleStatus) || !journey.lifecycleStatus
    ? t(lifecycleStatusKey(journey.lifecycleStatus))
    : String(journey.lifecycleStatus);
  const saveErrorText = saveError
    ? (saveError.key ? t(saveError.key) : resolveSafeUserVisibleError(saveError.cause, t, 'travel.common.actionFailed'))
    : '';
  // 日期依 app locale；純日期不經 UTC 轉換
  const dateRange = journey.startDate && journey.endDate && journey.startDate !== journey.endDate
    ? formatPlainDateRange(journey.startDate, journey.endDate, intlLocale)
    : formatPlainDate(journey.startDate || journey.endDate, intlLocale);
  const roleKey = `travel.journeyWorkspace.chrome.role.${journey.requesterRole}`;
  const roleLabel = journey.requesterRole && t(roleKey) !== roleKey ? t(roleKey) : journey.requesterRoleLabel;
  const actionLabel = (action) => {
    const { label } = lifecycleActionKeys(action);
    return label ? t(label) : String(action || '');
  };

  return (
    <main className="journey-workspace" data-read-source={readSource}>
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{chrome.eyebrow}</p>
          <h1>{journey.title}</h1>
          <dl className="journey-workspace__facts">
            {journey.destination ? <div><dt>{chrome.destination}</dt><dd>{journey.destination}</dd></div> : null}
            {dateRange ? <div><dt>{chrome.dates}</dt><dd>{dateRange}</dd></div> : null}
            <div>
              <dt>{chrome.status}</dt>
              <dd>
                <span
                  className="journey-workspace__lifecycle-badge"
                  data-lifecycle-status={journey.lifecycleStatus || 'UNKNOWN'}
                >
                  {statusLabel}
                </span>
              </dd>
            </div>
            {roleLabel ? <div><dt>{chrome.yourRole}</dt><dd>{roleLabel}</dd></div> : null}
          </dl>
        </div>
        <Link className="journey-workspace__back" to="/journeys">{chrome.myJourneys}</Link>
      </header>

      {offlineCached ? (
        <OfflineReadBanner
          fetchedAt={offlineFetchedAt}
          freshness={offlineFreshness || OFFLINE_FRESHNESS.STALE}
          onRetry={() => load()}
          retryBusy={phase === 'loading'}
        />
      ) : null}

      <JourneyWorkspaceNav
        journeyId={journey.journeyId}
        unreadCount={offlineCached ? null : unreadCount}
        offlineCached={offlineCached}
        todayOfflineAvailable={todayOfflineAvailable}
      />

      <JourneyContinuationSourceNote
        journeyId={journey.journeyId}
        requesterRole={offlineCached ? null : journey.requesterRole}
      />

      <div className="journey-workspace__body">
        {section === 'overview' ? (
          <section className="journey-workspace__panel" aria-labelledby="overview-heading">
            <div className="journey-workspace__panel-head">
              <h2 id="overview-heading">{chrome.overview}</h2>
              {journey.canEditBasic && !editing && !offlineCached ? (
                <button type="button" onClick={() => { setEditing(true); setSaveError(null); }}>{chrome.editBasicInfo}</button>
              ) : null}
            </div>
            {!offlineCached ? (
              <JourneyGuidedTasks
                journey={journey}
                schedule={schedule}
                scheduleKnown={spatialPhase === SPATIAL_PHASE.READY}
                accommodations={accommodations}
                transports={transports}
                onEditBasic={openBasicEditor}
              />
            ) : null}

            <p className="journey-workspace__panel-hint" data-testid="overview-hint">
              {t('travel.journeyWorkspace.overviewHint')}
            </p>

            {offlineCached && !todayOfflineAvailable ? (
              <p className="offline-read-today-missing" role="status" data-testid="overview-today-not-cached">
                {OFFLINE_READ_COPY.todayNotCached}
              </p>
            ) : null}

            {editing && !offlineCached ? (
              <form className="journey-workspace__edit" onSubmit={saveBasic} noValidate>
                <label htmlFor={titleId}>
                  {chrome.title}
                  <input id={titleId} value={draft.title} maxLength={100} onChange={(e) => setDraft((c) => ({ ...c, title: e.target.value }))} />
                </label>
                <label htmlFor={descriptionId}>
                  {chrome.description}
                  <textarea id={descriptionId} rows={4} value={draft.description} onChange={(e) => setDraft((c) => ({ ...c, description: e.target.value }))} />
                </label>
                <label htmlFor={destinationId}>
                  {chrome.destination}
                  <input id={destinationId} value={draft.destination} onChange={(e) => setDraft((c) => ({ ...c, destination: e.target.value }))} />
                </label>
                <div className="journey-workspace__dates">
                  <label htmlFor={startId}>
                    {chrome.startDate}
                    <input id={startId} type="date" value={draft.startDate} onChange={(e) => setDraft((c) => ({ ...c, startDate: e.target.value }))} />
                  </label>
                  <label htmlFor={endId}>
                    {chrome.endDate}
                    <input id={endId} type="date" value={draft.endDate} onChange={(e) => setDraft((c) => ({ ...c, endDate: e.target.value }))} />
                  </label>
                </div>
                {saveErrorText ? <p className="journey-workspace__error" role="alert">{saveErrorText}</p> : null}
                <div className="journey-workspace__edit-actions">
                  <button type="submit" disabled={busy || lifecyclePending}>{busy ? chrome.saving : chrome.saveChanges}</button>
                  <button type="button" disabled={busy || lifecyclePending} onClick={() => { setEditing(false); setSaveError(null); }}>{chrome.cancel}</button>
                </div>
              </form>
            ) : (
              <>
                <dl className="journey-workspace__overview">
                  <div><dt>{chrome.summary}</dt><dd>{journey.description || chrome.noDescription}</dd></div>
                  <div><dt>{chrome.destination}</dt><dd>{journey.destination || chrome.notSet}</dd></div>
                  <div><dt>{chrome.dates}</dt><dd>{dateRange || chrome.notSet}</dd></div>
                  <div><dt>{chrome.days}</dt><dd>{journey.dayCount}</dd></div>
                  <div><dt>{chrome.peopleCount}</dt><dd>{journey.memberCount}</dd></div>
                  {journey.createTime ? (
                    <div>
                      <dt>{chrome.created}</dt>
                      <dd>
                        <time dateTime={journey.createTime}>{formatDateTime(journey.createTime)}</time>
                      </dd>
                    </div>
                  ) : null}
                  {journey.updateTime ? (
                    <div>
                      <dt>{chrome.updated}</dt>
                      <dd>
                        <time dateTime={journey.updateTime}>{formatDateTime(journey.updateTime)}</time>
                      </dd>
                    </div>
                  ) : null}
                </dl>

                {/* PD-COV-01: Journey cover upload — uses existing PATCH coverFileReference.
                    Cover is NOT a SocialPost media item; displayOrder=0 rule does not apply. */}
                {journey.canEditBasic && !offlineCached ? (
                  <section
                    className="journey-workspace__cover"
                    aria-label={chrome.coverSectionLabel}
                    data-testid="journey-cover-section"
                  >
                    <h3>{chrome.coverSectionLabel}</h3>
                    {journey.coverUrl ? (
                      <img
                        src={journey.coverUrl}
                        alt={chrome.coverAlt}
                        className="journey-workspace__cover-img"
                      />
                    ) : (
                      <p className="journey-workspace__cover-empty">{chrome.coverEmpty}</p>
                    )}
                    {coverPhase === 'uploading' ? (
                      <p className="journey-workspace__cover-status" role="status">
                        {chrome.coverUploading}
                      </p>
                    ) : null}
                    {coverPhase === 'error' ? (
                      <p className="journey-workspace__error" role="alert">{coverError ? t(coverError) : null}</p>
                    ) : null}
                    <div className="journey-workspace__cover-actions">
                      {/* Hidden file input — triggered by button below */}
                      <input
                        ref={coverInputRef}
                        type="file"
                        accept="image/*"
                        className="journey-workspace__cover-input"
                        aria-label={chrome.coverUploadLabel}
                        onChange={handleCoverFileChange}
                        data-testid="journey-cover-input"
                      />
                      {coverPhase === 'error' ? (
                        <button
                          type="button"
                          onClick={retryCoverUpload}
                          data-testid="journey-cover-retry"
                        >
                          {chrome.tryAgain}
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={coverPhase === 'uploading'}
                          aria-busy={coverPhase === 'uploading'}
                          onClick={() => coverInputRef.current?.click()}
                          data-testid="journey-cover-upload-btn"
                        >
                          {coverPhase === 'uploading' ? chrome.coverUploading : chrome.coverUploadLabel}
                        </button>
                      )}
                    </div>
                  </section>
                ) : null}
              </>
            )}

            <section className="journey-workspace__lifecycle" aria-labelledby={lifecycleTitleId}>
              <h3 id={lifecycleTitleId}>{t('travel.journeyWorkspace.lifecycle.heading')}</h3>
              <p
                className="journey-workspace__lifecycle-status"
                data-lifecycle-status={journey.lifecycleStatus || 'UNKNOWN'}
              >
                <strong>{statusLabel}</strong>
              </p>
              <p className="journey-workspace__lifecycle-hint">{t(lifecycleHintKey(journey.lifecycleStatus))}</p>
              {lifecycleError ? (
                <p className="journey-workspace__error" role="alert">{t(lifecycleError)}</p>
              ) : null}
              {offlineCached ? (
                <p className="offline-read-network-required" role="status">
                  {t('travel.journeyWorkspace.nav.networkRequired')}
                </p>
              ) : (
                <>
                  <div className="journey-workspace__lifecycle-actions">
                    {primaryAction ? (
                      <button
                        type="button"
                        className="journey-workspace__lifecycle-primary"
                        disabled={lifecyclePending || busy}
                        onClick={() => openLifecycleConfirm(primaryAction)}
                      >
                        {lifecyclePending && confirmAction === primaryAction
                          ? t('travel.journeyWorkspace.updating')
                          : actionLabel(primaryAction)}
                      </button>
                    ) : null}
                  </div>
                  {secondaryActions.length ? (
                    <div className="journey-workspace__lifecycle-secondary" aria-label={t('travel.journeyWorkspace.lifecycle.secondaryLabel')}>
                      {secondaryActions.map((action) => (
                        <button
                          key={action}
                          type="button"
                          className={action === 'CANCEL' || action === 'ARCHIVE'
                            ? 'journey-workspace__lifecycle-danger'
                            : undefined}
                          disabled={lifecyclePending || busy}
                          onClick={() => openLifecycleConfirm(action)}
                        >
                          {actionLabel(action)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </section>

            <JourneySpatialOverviewSection
              journeyId={journey.journeyId}
              spatialPhase={spatialPhase}
              schedule={schedule}
              accommodations={accommodations}
              transports={transports}
              offlineCached={offlineCached}
            />

            {!offlineCached ? (
              <JourneyPublicationSection
                key={journey.journeyId}
                journeyId={journey.journeyId}
                requesterRole={journey.requesterRole}
                lifecycleStatus={journey.lifecycleStatus}
                journeyRowVersion={journey.rowVersion}
                itineraryRowVersion={schedule?.itineraryRowVersion ?? null}
                disabled={lifecyclePending || busy}
              />
            ) : null}

            <aside className="journey-workspace__next" id="journey-all-tools" tabIndex={-1} data-testid="journey-all-tools">
              <h3>{t('travel.journeyWorkspace.allTools.title')}</h3>
              {offlineCached ? (
                <p className="offline-read-network-required" role="status">
                  {t('travel.journeyWorkspace.nav.networkRequired')}
                  {' — '}
                  {t('travel.journeyWorkspace.allTools.offlineDetail')}
                </p>
              ) : (
                <>
                  <p>{t('travel.journeyWorkspace.allTools.intro')}</p>
                  {/* 每個連結搭配獨立說明，不以 Link 拼接句子；目的地不變 */}
                  <ul className="journey-workspace__tool-list">
                    {['planning', 'stays', 'transport', 'checklist', 'budget'].map((tool) => (
                      <li key={tool}>
                        <Link to={`/journeys/${journey.journeyId}/${tool}`}>{t(`travel.journeyWorkspace.nav.item.${tool}`)}</Link>
                        <span>{t(`travel.journeyWorkspace.allTools.${tool}`)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="journey-decision__overview-cta">
                    <Link to={`/journeys/${journey.journeyId}/decisions`}>{t('travel.journeyWorkspace.allTools.decisions')}</Link>
                  </p>
                  <p className="journey-journal__overview-cta">
                    <Link to={`/journeys/${journey.journeyId}/journal`}>{t('travel.journeyWorkspace.recordJourney')}</Link>
                  </p>
                </>
              )}
            </aside>
          </section>
        ) : null}

        {section === 'people' ? (
          <section className="journey-workspace__panel" aria-labelledby="people-heading">
            <h2 id="people-heading">{chrome.people}</h2>
            {offlineCached ? (
              <p className="offline-read-network-required" role="status">
                {OFFLINE_READ_COPY.networkRequired}
              </p>
            ) : (
              <JourneyPeopleInviteSection
                journey={journey}
                disabled={lifecyclePending || busy}
                onMembersRefresh={() => load({ soft: true })}
              />
            )}
            <JourneyPeopleMembershipPanel
              journey={journey}
              viewerMemberId={viewerMemberId}
              offlineCached={offlineCached}
              disabled={lifecyclePending || busy}
              memberSummaries={memberSummaries}
              onMembersRefresh={() => load({ soft: true })}
            />
          </section>
        ) : null}
      </div>

      {confirmAction && confirmCopy && !offlineCached ? (
        <div className="journey-workspace-lifecycle-confirm" role="presentation">
          <button
            type="button"
            className="journey-workspace-lifecycle-confirm__backdrop"
            aria-label={t('travel.journeyWorkspace.lifecycle.closeConfirm')}
            disabled={lifecyclePending}
            onClick={closeLifecycleConfirm}
          />
          <div
            className="journey-workspace-lifecycle-confirm__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={confirmTitleId}
          >
            <h3 id={confirmTitleId}>{t(confirmCopy.title)}</h3>
            <p>{t(confirmCopy.body)}</p>
            {confirmAction === 'CANCEL' ? (
              <label htmlFor={cancelReasonId}>
                {t('travel.journeyWorkspace.lifecycle.cancelReason')}
                <textarea
                  id={cancelReasonId}
                  rows={3}
                  maxLength={500}
                  value={cancelReason}
                  disabled={lifecyclePending}
                  onChange={(event) => setCancelReason(event.target.value)}
                />
              </label>
            ) : null}
            {lifecycleError ? (
              <p className="journey-workspace__error" role="alert">{t(lifecycleError)}</p>
            ) : null}
            <div className="journey-workspace-lifecycle-confirm__actions">
              <button type="button" disabled={lifecyclePending} onClick={closeLifecycleConfirm}>
                {t('travel.journeyWorkspace.lifecycle.back')}
              </button>
              <button
                type="button"
                className={confirmAction === 'CANCEL' || confirmAction === 'ARCHIVE'
                  ? 'journey-workspace__lifecycle-danger'
                  : 'journey-workspace__lifecycle-primary'}
                disabled={lifecyclePending}
                onClick={submitLifecycle}
              >
                {lifecyclePending ? t('travel.journeyWorkspace.updating') : t(confirmCopy.confirm)}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
